import { apiFetch } from './client'
import { auth } from '../lib/firebase'

export type Scope = 'org' | 'personal'

export interface KbDocument {
  source: string
  sourceType: string
  scope: Scope
  uploadedAt: string | null
  /** Who added it. Only they may change who sees it. */
  uid: string | null
  /** False for text ingested through the API — chunks with no file behind them. */
  hasOriginal: boolean
  /** Set when this came from a linked page rather than an uploaded file. */
  url: string | null
  chunks: number
}

export const SUPPORTED_EXTENSIONS = ['pdf', 'docx', 'txt', 'md']
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024

/** How much of each allowance is spent. Uploads and links are counted apart. */
export interface KbLimits {
  documents: { used: number; max: number }
  links: { used: number; max: number }
}

export function listDocuments() {
  return apiFetch<{ documents: KbDocument[]; orgId: string; limits: KbLimits }>('listDocuments')
}

export function deleteDocument(source: string) {
  return apiFetch<{ removed: number; filesRemoved: number }>('deleteDocument', { source })
}

/**
 * Goes through the Function, not a bucket URL, so the download is subject to the
 * same visibility check as search. The auth header is why this cannot simply be
 * an <a href>.
 */
export async function downloadDocument(source: string) {
  const token = await auth.currentUser?.getIdToken()

  const res = await fetch(`/api/downloadDocument?source=${encodeURIComponent(source)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!res.ok) {
    const msg = await res.text().catch(() => '')
    throw new Error(`Download failed (${res.status}): ${msg.slice(0, 120)}`)
  }

  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = source
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * Read the file in the browser and send it as base64 JSON.
 *
 * Multipart would avoid the ~33% base64 overhead, but it would mean parsing
 * multipart in the Function for one small upload. The 5MB cap keeps the encoded
 * payload well under the Cloud Functions request limit.
 */
export function uploadDocument(file: File, scope: Scope, sourceType: string) {
  return new Promise<{ chunks: number; scope: Scope; storedOriginal: boolean }>((resolve, reject) => {
    const reader = new FileReader()

    reader.onerror = () => reject(new Error('Could not read that file'))
    reader.onload = () => {
      // strip the "data:...;base64," prefix
      const base64 = String(reader.result).split(',')[1] ?? ''
      apiFetch<{ chunks: number; scope: Scope; storedOriginal: boolean }>('ingestDocument', {
        filename: file.name,
        contentBase64: base64,
        source: file.name,
        sourceType,
        scope,
      }).then(resolve, reject)
    }

    reader.readAsDataURL(file)
  })
}

export interface LinkedPage {
  source: string
  url: string
  chunks: number
  pictures: number
  scope: Scope
  /** Why no screenshots were read, when the agent asked for them. */
  pictureNote: string | null
}

/** Move a saved file or page between "only me" and "everyone". */
export const setDocumentScope = (source: string, scope: Scope) =>
  apiFetch<{ ok: true; scope: Scope }>('setDocumentScope', { source, scope })

export const ingestUrl = (
  url: string,
  scope: Scope,
  describePictures: boolean,
  /** The page this one takes the place of, so it does not count twice. */
  replaces?: string,
) => apiFetch<LinkedPage>('ingestUrl', { url, scope, describePictures, replaces })
